import { useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { useChat } from "@/contexts/ChatContext";
import {
  useCreateChatConversationMutation,
  useGetChatUsersQuery,
} from "@/services/chat";
import { ConversationView } from "@/interfaces/Chat";
export const chatError = (error: any) => {
  if (
    error?.status === 409 &&
    (error?.data?.code === "CHAT_LAST_ACTIVE_ADMIN" ||
      error?.data?.message === "CHAT_LAST_ACTIVE_ADMIN")
  )
    return "Désignez un autre administrateur actif avant cette action.";
  if (error?.status === 409)
    return "La conversation a changé. Vérifiez les informations avant de réessayer.";
  if (error?.status === 429)
    return "Trop de demandes. Patientez quelques instants puis réessayez.";
  if (error?.status === 403 || error?.status === 404)
    return "Vous n’avez plus l’autorisation d’effectuer cette action.";
  return error?.data?.message && !/^[A-Z_]+$/.test(error.data.message)
    ? error.data.message
    : "Cette action a échoué. Réessayez.";
};
export function ChatUserPicker({
  selected,
  onChange,
  multiple = true,
}: {
  selected: string[];
  onChange: (ids: string[]) => void;
  multiple?: boolean;
}) {
  const { actorId } = useChat();
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState<string | undefined>();
  const { currentData, isFetching, isError } = useGetChatUsersQuery(
    { actorId: actorId!, q, cursor },
    { skip: !actorId },
  );
  return (
    <div className="chat-user-picker">
      <Form.Label htmlFor="chat-user-search">
        Rechercher un utilisateur
      </Form.Label>
      <Form.Control
        id="chat-user-search"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setCursor(undefined);
        }}
        placeholder="Nom ou adresse e-mail"
      />
      <div className="chat-picker-results" aria-busy={isFetching}>
        {currentData?.data
          .filter((u) => u.id !== actorId)
          .map((u) => (
            <Form.Check
              key={u.id!}
              id={`chat-user-${u.id}`}
              type={multiple ? "checkbox" : "radio"}
              name="chat-users"
              label={`${u.name} — ${u.email}`}
              checked={selected.includes(u.id!)}
              onChange={() =>
                onChange(
                  multiple
                    ? selected.includes(u.id!)
                      ? selected.filter((id) => id !== u.id)
                      : [...selected, u.id!]
                    : [u.id!],
                )
              }
            />
          ))}
        {isError && (
          <Alert variant="danger">Impossible de charger l’annuaire.</Alert>
        )}
        {!isFetching && currentData?.data.length === 0 && (
          <p>Aucun utilisateur trouvé.</p>
        )}
      </div>
      <div className="d-flex gap-2">
        {cursor && (
          <Button size="sm" variant="link" onClick={() => setCursor(undefined)}>
            Début de la liste
          </Button>
        )}
        {currentData?.nextCursor && (
          <Button
            size="sm"
            variant="link"
            onClick={() => setCursor(currentData.nextCursor!)}
          >
            Utilisateurs suivants
          </Button>
        )}
      </div>
      {selected.length > 0 && (
        <p className="mt-2">
          {selected.length} personne{selected.length > 1 ? "s" : ""}{" "}
          sélectionnée{selected.length > 1 ? "s" : ""}{" "}
          <Button size="sm" variant="link" onClick={() => onChange([])}>
            Effacer
          </Button>
        </p>
      )}
    </div>
  );
}
export default function CreateConversationModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (conversation: ConversationView) => void;
}) {
  const { actorId } = useChat();
  const [kind, setKind] = useState<"direct" | "group">("direct");
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [create, { isLoading }] = useCreateChatConversationMutation();
  const submit = async () => {
    setError("");
    try {
      const result = await create({
        actorId: actorId!,
        body:
          kind === "group"
            ? { kind, name: name.trim(), memberIds: selected }
            : { kind, otherUserId: selected[0] },
      }).unwrap();
      onCreated(result);
    } catch (e) {
      setError(chatError(e));
    }
  };
  return (
    <Modal show onHide={onClose} centered aria-labelledby="chat-create-title">
      <Modal.Header closeButton>
        <Modal.Title id="chat-create-title">Nouvelle conversation</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="d-flex gap-3 mb-3">
          <Form.Check
            inline
            type="radio"
            id="chat-direct"
            label="Message privé"
            checked={kind === "direct"}
            onChange={() => {
              setKind("direct");
              setSelected([]);
            }}
          />
          <Form.Check
            inline
            type="radio"
            id="chat-group"
            label="Groupe"
            checked={kind === "group"}
            onChange={() => {
              setKind("group");
              setSelected([]);
            }}
          />
        </div>
        {kind === "group" && (
          <Form.Group className="mb-3" controlId="chat-group-name">
            <Form.Label>Nom du groupe</Form.Label>
            <Form.Control
              maxLength={100}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Form.Group>
        )}
        <ChatUserPicker
          selected={selected}
          onChange={setSelected}
          multiple={kind === "group"}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          Annuler
        </Button>
        <Button
          disabled={
            isLoading || !selected.length || (kind === "group" && !name.trim())
          }
          onClick={submit}
        >
          {isLoading ? "Création…" : "Créer"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
